import { Formik, useField } from 'formik';
import * as React from 'react';
import { IService, IXtimeServiceBase, useLocalizedServiceFactory } from '../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist } from '../../context/Appointment/appointment';
import { Types, useCart, useCartDispatch } from '../../context/cart';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization } from '../../context/Localization/localization';
import { DetailCard } from '../../controls/DetailCard/DetailCard';
import { Heading } from '../../controls/Heading/Heading';
import { getUpdateAptServices } from '../../helpers/Appointment';
import { nameof } from '../../helpers/nameof';
import { Spinner } from '../../controls/Spinner/Spinner';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { GMForm, GMFormWrapper } from '../../controls/Form';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';

interface IProps {
    onProceed: () => void;
    onSkip: () => void;
}

export const Comments = (props: IProps) => {
    const cart = useCart();
    const cartDispatch = useCartDispatch();
    const {
        dealer: { serviceCommentsEnabled },
    } = useKioskState();
    const appointment = useAppointmentState();
    const setAppointment = useAppointmentUpdateAndPersist();
    const [saveAppointment, setSaveAppointment] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const Localization = useLocalization();
    const serviceFactory = useLocalizedServiceFactory();
    const serviceItems = [...cart.services.map(serviceFactory), ...cart.xtime.services]
        .filter((s) => s.isCommonRepair)
        .sort((a, b) => {
            const aName = 'localization' in a ? a.localization.name : a.name;
            const bName = 'localization' in b ? b.localization.name : b.name;

            return aName.localeCompare(bName);
        });

    if (!serviceItems.length || !serviceCommentsEnabled) {
        props.onSkip();
    }

    const initialValues = {
        serviceItems: serviceItems.map((s, index) => {
            const isRequired = !!s.opCode && s.opCode.length > 0;
            const isStandardService = 'category' in s;

            return {
                ...s,
                meta: {
                    isRequired,
                    isStandardService,
                    key: '' + isStandardService + s.id,
                },
            };
        }),
    };

    React.useEffect(() => {
        if (saveAppointment && !loading) {
            setLoading(true);
            const isMobileCheckIn = !isElectron() && !!appointment.mobileCheckin;
            setAppointment(
                {
                    ...appointment,
                    servicesConfirmed: getUpdateAptServices(cart.services),
                    xtimeServices: cart.xtime.services,
                },
                false,
                false,
                false,
                true,
                undefined,
                isMobileCheckIn,
                false,
                isMobileCheckIn,
            )
                .then(() => {
                    setSaveAppointment(false);
                    setLoading(false);
                })
                .then(props.onProceed);
        }
    }, [appointment, cart.services, cart.xtime.services, loading, props.onProceed, saveAppointment, setAppointment]);

    return !serviceItems.length || !serviceCommentsEnabled ? (
        <Spinner />
    ) : (
        <Formik
            initialValues={initialValues}
            onSubmit={(values) => {
                values.serviceItems.forEach((s) => {
                    const { meta, ...newServiceItem } = s;
                    // TODO: figure out how to get Typescript to key off meta.isStandardService
                    if ('full' in newServiceItem) {
                        const service = { ...newServiceItem.full,
                            comment: newServiceItem.comment };
                        cartDispatch({ type: Types.UpdateService,
                            payload: service });
                    }
                    else {
                        cartDispatch({ type: Types.UpdateXtimeService,
                            payload: newServiceItem });
                    }
                });
                setSaveAppointment(true);
            }}
        >
            {({ values, submitForm, isValid }) => (
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={<GMAppointmentName />}
                            secondaryHeading={Localization.AddDetailsToServiceItems}
                            hint={Localization.PleaseEnterYourAdditionalDetails}
                            leftAlign
                        />
                    }
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            onClick={submitForm}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            disabled={!isValid}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />
                    }
                >
                    {values.serviceItems.map((s, index) => (
                        <ServiceItemLineComment
                            id={s.meta.key}
                            key={s.meta.key}
                            // NOTE: This will fail if the items can be deleteable in the future. Consider using <FieldArray> wrapper
                            name={`${nameof<typeof values>('serviceItems')}.${index}`}
                            index={index}
                            isRequired={s.meta.isRequired}
                            serviceItem={'full' in s ? s.full : s}
                        />
                    ))}
                </GMForm>
            )}
        </Formik>
    );
};

interface IServiceItemLineComment {
    index: number;
    name: string;
    id: string;
    /** The service item to display */
    serviceItem: IXtimeServiceBase | IService;
    /** Whether the displayed item is required to have a comment */
    isRequired: boolean;
}

const ServiceItemLineComment = (props: IServiceItemLineComment) => {
    const Localization = useLocalization();
    const serviceFactory = useLocalizedServiceFactory();

    const validate = (value: typeof props.serviceItem) => {
        const isValid = !props.isRequired || (!!value.comment && value.comment.length > 0);

        return isValid ? undefined : Localization.AdditionalCommentsRequired;
    };

    const [
        {
            value: { comment, ...rest },
            onBlur,
        },
        { error },
        { setValue },
    ] = useField<typeof props.serviceItem>({ name: props.name,
        validate });

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setValue({ ...rest,
            comment: e.target.value });
    };

    return (
        <GMFormWrapper fullWidth>
            <DetailCard
                id={props.id}
                title={'category' in props.serviceItem ? serviceFactory(props.serviceItem).localization.name : props.serviceItem.name}
                label={''}
                ariaLabel={'category' in props.serviceItem ? serviceFactory(props.serviceItem).localization.name : props.serviceItem.name}
                index={props.index + 1}
                name={props.name}
                details={comment || ''}
                editable
                placeholder={(props.isRequired && Localization.AdditionalCommentsRequired) || Localization.NoComment}
                onChangeCallback={handleChange}
                onBlurCallback={() => onBlur}
                error={error}
            />
        </GMFormWrapper>
    );
};
